import { useParams } from "react-router";

import { Autocomplete, Box, FormControl, Grid, Stack, TextField, Typography } from "@mui/material";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import { ArcPlcIcon } from "components/Icons/Icons";
import { BASE_ACRES_COMMODITY_NAMES } from "components/Vault/Farm/helpers";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import dayjs from "dayjs";

import { FARM_PAYMENT_CATEGORIES } from "./FarmPaymentConstants";
import { useFarmPaymentForm } from "./useFarmPaymentForm";

export default function FarmPaymentEditWorkspace() {
  const { farm_payment_id } = useParams();
  const formMaxWidth = "800px";

  const { state, formStatusComponent, handleChangeStateSelect, handleChangeState } = useFarmPaymentForm();

  const headerTitle =
    String(farm_payment_id) === "0"
      ? "Add Payment"
      : `${getCategoryLabel(state?.payment_category)} - ${formatPaymentDate(state?.payment_date)}`;

  return (
    <>
      <WorkspaceHeader
        title={headerTitle}
        category={{
          title: "ARC/PLC",
          icon: <ArcPlcIcon />,
        }}
        breadCrumbs={[
          {
            title: "ARC/PLC",
            path: "/arcplc",
          },
          {
            title: "Payments",
            path: "/arcplc/payments",
          },
        ]}
      />

      <Stack direction="row" spacing={2} className="headerWithMenuButton">
        <Typography variant="h1">Payment</Typography>
        {formStatusComponent}
      </Stack>

      <Box sx={{ maxWidth: formMaxWidth }}>
        <Grid container spacing={1} rowGap={2}>
          <Grid item xs={12} md={6} className="autocomplete_item">
            <FormControl fullWidth>
              <Autocomplete
                value={FARM_PAYMENT_CATEGORIES.find((category) => category.value === state.payment_category) || null}
                onChange={(event, newValue) => {
                  handleChangeStateSelect({ target: { name: "payment_category", value: newValue?.value ?? null } });
                }}
                autoSelect
                autoHighlight
                options={FARM_PAYMENT_CATEGORIES}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payment Category"
                    name="payment_category"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Payment Amount"
                name="payment_amount"
                defaultValue={state.payment_amount ?? 0}
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} className="claim_date_grid">
            <TilleyDatePicker
              name="payment_date"
              label="Payment Date"
              value={state?.payment_date}
              onChange={handleChangeState}
              className="payment_date_picker"
            />
          </Grid>
          <Grid item xs={12} md={6} className="autocomplete_item">
            <FormControl fullWidth>
              <Autocomplete
                value={state.commodity_name || null}
                onChange={(event, newValue) => {
                  handleChangeStateSelect({ target: { name: "commodity_name", value: newValue ?? null } });
                }}
                autoSelect
                autoHighlight
                options={BASE_ACRES_COMMODITY_NAMES}
                getOptionLabel={(option) => option || ""}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Commodity (optional)"
                    name="commodity_name"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Note"
                name="note"
                defaultValue={state.note || ""}
                onChange={handleChangeState}
                inputProps={{ maxLength: 100 }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

function getCategoryLabel(value) {
  return FARM_PAYMENT_CATEGORIES.find((category) => category.value === value)?.label ?? "Payment";
}

function formatPaymentDate(value) {
  return value ? dayjs(value).format("MM/DD/YYYY") : "No Date";
}
